import { ButtonProps } from '@mui/material'
import { Asset, AssetFluid } from './asset'
import { Brand } from './brand'
import { JumpLink, Post, PostResumeSample, RichText } from './post'
import { LandingPage, PostLink } from './landingPage'
import { Modal } from './modal'
import { Category } from './category'
import { ABTest, ButtonActionABTest } from './abTest'
import { LandingPageV2, CustomComponent } from './landingPageV2'
import { ReviewPage } from './review'
import { FAQPage } from './faq'
import { ProductPackage } from './productPageV2'
import { ReactNode } from 'react'
import { IResumeUpload } from './widget'
import { MixpanelTracking } from './mixpanel'
import { HrefLang } from 'components/Hreflang/Hreflang'

export type ActionLinkPost = Pick<
  Post | PostResumeSample,
  'slug' | 'title' | 'contentful_id' | '__typename'
>

export type ActionLinkLandingPage = Pick<
  LandingPage | LandingPageV2 | FAQPage | ReviewPage,
  'slug' | 'title' | 'contentful_id' | '__typename'
>

export type ActionLink =
  | ActionLinkPost
  | ActionLinkLandingPage
  | Modal
  | ExternalLink
  | Category
  | ABTest<ButtonActionABTest>
  | JumpLink
  | InternalLink
  | null
  | IResumeUpload

type InternalLinkAction =
  | ActionLinkPost
  | ActionLinkLandingPage
  | Modal
  | ExternalLink
  | Category
  | ABTest<ButtonActionABTest>
  | JumpLink
  | null

export interface ExternalLink {
  contentful_id: string
  text: string | undefined
  url: string
  __typename: 'ContentfulExternalLink'
  icon: AssetFluid | null
  openTheLinkIn: string | null
  stagingUrl: string | null
  devUrl: string | null
  codeClasses: string[] | null
  mixpanelTracking: MixpanelTracking | null
}

export enum TargetOptions {
  NewTab = 'New tab',
  SameTab = 'Same tab',
}

export interface InternalLink {
  contentful_id: string
  text: string | undefined
  action: InternalLinkAction
  __typename: 'ContentfulInternalLink'
  slug: string | null
  icon: AssetFluid | null
  codeClasses: string[] | null
  mixpanelTracking: MixpanelTracking | null
}

export enum ButtonRole {
  Submit = 'Submit',
  Dismiss = 'Dismiss',
  EmailInput = 'Email Input',
  ResumeUpload = 'Resume Upload',
  OpenIntercom = 'Open Intercom',
  LanguageSwitcher = 'Language Switcher',
}

export interface Button<ActionType = ActionLink> {
  contentful_id: string
  buttonText: string
  action: ActionType
  role?: ButtonRole | null
  color?: ButtonProps['color']
  variant?: ButtonProps['variant']
  __typename: 'ContentfulButton'
  onClick?: React.MouseEventHandler | (() => void)
  codeClasses: string[] | null
  mixpanelTracking: MixpanelTracking
}

export interface PurchaseButton
  extends Omit<Button<ExternalLink | InternalLink>, 'role' | '__typename'> {
  __typename: 'ContentfulPurchaseButton'
  // I don't know why the NavigationGroupLink `link.action` infers PurchaseButton
  // https://github.com/TalentInc/products-v2/blob/4d3d407a253cb2bb6d125f3512c2b2d06a54b970/ecommerce/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupLink.tsx#L90
  text?: string
  // @TODO: Clean this up and rectify the type definitions
}

export type NavigationGroupLink =
  | ExternalLink
  | InternalLink
  | Button
  | PurchaseButton
  | Category
  | LandingPage
  | Post

export interface NavigationGroup<T = NavigationGroupLink> {
  title: string
  text: string
  links: T[]
  contentful_id: string
  category: Category | null
  variant: 'Dropdown' | null
  __typename: 'ContentfulNavigationGroup'
  link?: InternalLink | ExternalLink | null
}

export interface Header {
  name: string
  logo: Asset | null
  navigation: NavigationGroup[]
}

export interface HeaderV2 {
  contentful_id: string
  name: string
  navigationLeftSide: NavigationGroup[]
  navigationRightSide: (NavigationGroup | Button | CustomComponent)[]
  hideBuyNowButton: Array<string> | null
}

export interface Footer {
  contentful_id: string
  name: string
  badge: Asset
  phoneText: string
  creditCards: Asset[]
  bottomNavigation: NavigationGroup[]
  navigation: NavigationGroup[]
  showNewsletterForm: boolean | null
}

export interface LocaleNode {
  language: string
}

export interface LocaleEdge {
  node: LocaleNode
}

export interface Locales {
  edges: LocaleEdge[]
}

export interface PageDataContextType {
  brand: Brand
  headerV2?: HeaderV2
  navTestHeaderV2?: HeaderV2
  header?: Header
  footer?: Footer
  locales?: Locales
  discount?: string | null
  children: ReactNode
  authorPosts: AuthorPosts
}

export interface SEO {
  pageTitle: string | null
  description: string | null
  openGraphTitle?: string | null
  openGraphDescription?: string | null
  openGraphImage?: Asset | null
  noIndex?: boolean
  pageType?: PageType | null
  doNotDeployToProduction?: boolean | null
  canonicalUrl?: string | null
}

export enum PageType {
  About = 'About',
  Contact = 'Contact',
}

export interface PriceComponent {
  name: string
  price: string
  contentful_id: string
  __typename: 'ContentfulPriceComponent'
  url: string
}

export interface PostCardsComponent {
  name: string
  contentful_id: string
  __typename: 'ContentfulPostCards'
  posts: PostLink[]
}

export interface ReferenceTextCopy {
  name: string
  contentful_id: string
  key: string
  __typename: 'ContentfulReferencedTextCopy'
  reference: ProductPackage
}

export enum TrustPilotRatingWidgetVariants {
  FiveStars = 'Five Stars',
  Qualitative = 'Qualitative',
  Quantitative = 'Quantitative',
  QualitativePricingPage = 'Qualitative Pricing Page',
  QuantitativePricingPageCritique = 'Quantitative Pricing Page Critique',
  FiveStarsAlternative = 'FiveStars Alternative',
  FiveStarsDescriptive = 'FiveStars Descriptive',
}

export interface TrustPilotRatingWidget {
  brand: string
  variant: TrustPilotRatingWidgetVariants | null
  contentful_id: string
  __typename: 'ContentfulTrustPilotRatingWidget'
}

export interface ArticlesDisplayComponent {
  contentful_id: string
  __typename: 'ContentfulArticlesDisplay'
  title: string
  link: ExternalLink | InternalLink
  articles: (ArticleComponent | Post)[]
}
export interface ArticleComponent {
  contentful_id: string
  __typename: 'ContentfulArticle'
  title: string
  excerpt: RichText
  featuredImage: AssetFluid
  slug: string
}

export enum NavTitles {
  Account = 'Account',
}

export interface PageContextType {
  id?: string
  slug?: string
  brandName?: string
  categorySlug?: string
  noIndex?: boolean
  hreflangs?: HrefLang[]
}

export interface PageDataType {
  [key: string]: unknown
  seo: Required<SEO>
}

export interface AuthorPosts {
  nodes: PostLink[]
}
